/** Latest-query-wins pagination. Invalidation happens on the keystroke, before debounce. */
export interface SearchPage<T> {
  rows: T[];
  next_offset: number | null;
  has_more: boolean;
}
export interface PagedSearchState<T> {
  rows: T[];
  query: string;
  loading: boolean;
  hasMore: boolean;
  error: unknown;
}
export function createPagedSearch<T extends { id: string }>(deps: {
  search: (
    query: string,
    offset: number,
    signal: AbortSignal
  ) => Promise<SearchPage<T>>;
  onChange: (state: PagedSearchState<T>) => void;
  debounceMs?: number;
}) {
  let state: PagedSearchState<T> = {
    rows: [],
    query: '',
    loading: false,
    hasMore: false,
    error: null,
  };
  let sequence = 0;
  let offset = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;
  const emit = () => deps.onChange({ ...state });
  const invalidate = () => {
    sequence++;
    clearTimeout(timer);
    controller?.abort();
  };
  const fetchPage = async (seq: number, start: number) => {
    controller = new AbortController();
    try {
      const page = await deps.search(state.query, start, controller.signal);
      if (seq !== sequence) return;
      const rows = new Map((start ? state.rows : []).map(r => [r.id, r]));
      page.rows.forEach(r => rows.set(r.id, r));
      offset = page.next_offset ?? 0;
      state = {
        ...state,
        rows: [...rows.values()],
        loading: false,
        hasMore: page.has_more,
        error: null,
      };
    } catch (error) {
      if (seq !== sequence) return;
      state = { ...state, loading: false, error };
    }
    emit();
  };
  return {
    query(query: string) {
      invalidate();
      offset = 0;
      state = {
        rows: [],
        query: query.trim(),
        loading: !!query.trim(),
        hasMore: false,
        error: null,
      };
      emit();
      if (state.query) {
        const seq = sequence;
        timer = setTimeout(() => {
          fetchPage(seq, 0).catch(console.error);
        }, deps.debounceMs ?? 250);
      }
    },
    loadMore() {
      if (state.loading || (!state.hasMore && !state.error)) return;
      state = { ...state, loading: true, error: null };
      emit();
      fetchPage(sequence, offset).catch(console.error);
    },
    dispose() {
      invalidate();
    },
  };
}
